import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/site";

export default function ServicesGrid() {
  return (
    <section id="services" className="py-24 md:py-36 bg-white" data-testid="services-section">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 items-end">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="md:col-span-8"
          >
            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--text-2)] mb-6">
              — What We Print
            </div>
            <h2 className="font-display text-[46px] md:text-[86px] leading-[0.98] tracking-[-0.02em]">
              Services shaped by <em className="italic text-[color:var(--accent)]">three decades</em> of press-floor practice.
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-4 md:pl-6 text-[color:var(--text-2)] text-[16px] leading-[1.6] md:pb-4"
          >
            From identity to publishing to large-format signage — a full-service printer for businesses, publishers, organisations, professionals, and individuals.
          </motion.p>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.09, ease: [0.7, 0, 0.2, 1] }}
              className="group relative rounded-2xl overflow-hidden bg-[color:var(--bg)] border border-black/5 hover:border-black/15 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-25px_rgba(0,0,0,0.25)] card-sweep"
              data-testid={`service-card-${s.slug}`}
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.08]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/0" />
                <div className="absolute top-4 left-4 flex items-center gap-2 rounded-full bg-white/85 backdrop-blur px-3 py-1.5 font-mono text-[10px] tracking-[0.22em] uppercase">
                  {s.number} · {s.tag}
                </div>
              </div>
              <div className="p-7 md:p-8">
                <h3 className="font-display text-3xl md:text-[32px] leading-tight tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-[color:var(--text-2)] text-[15px] leading-[1.6]">{s.blurb}</p>
                <ul className="mt-5 space-y-1.5 text-[13.5px] text-[color:var(--text-2)]">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="mt-2 w-1 h-1 rounded-full bg-[color:var(--accent)]" />{f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/quote"
                  className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-medium text-[color:var(--text)] group/link"
                  data-testid={`service-cta-${s.slug}`}
                >
                  <span className="link-underline">Request a Quote</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
